import {
  FieldsPrefillData,
  FormPrefillType,
  SkpEnvironment,
  MailboxDetails,
  ProtectionType,
  TransferDraft,
  TransferDraftErrors,
  TransferDraftFile,
  TransferReceiver,
} from "../entities";
import { assertNever } from "../utils/typeGuard";
import { featureFlags } from "../utils/featureFlags";

type InitializationAction = {
  type: TransferDraftActionType.initialize;
  mailboxDetails: MailboxDetails;
  environment: SkpEnvironment;
};

type ResetAction = {
  type: TransferDraftActionType.reset;
  environment: SkpEnvironment;
};

type FilesAddAction = {
  type: TransferDraftActionType.filesAdd;
  addedFiles: File[];
};

type FilesRemoveAction = {
  type: TransferDraftActionType.filesRemove;
  removedFiles: TransferDraftFile[];
};

type SenderNameUpdateAction = {
  type: TransferDraftActionType.senderNameUpdate;
  updatedName: string;
};

type SenderEmailUpdateAction = {
  type: TransferDraftActionType.senderEmailUpdate;
  updatedEmail: string;
};

type SubjectUpdateAction = {
  type: TransferDraftActionType.subjectUpdate;
  updatedSubject: string;
};

type DescriptionUpdateAction = {
  type: TransferDraftActionType.descriptionUpdate;
  updatedDescription: string;
};

type ErrorUpdateAction = {
  type: TransferDraftActionType.errorUpdate;
  postValidationErrors: Set<TransferDraftErrors>;
};

type SenderEmailValidationAction = {
  type: TransferDraftActionType.senderEmailValidate;
  inputtedSenderEmail: string;
};

type SenderNameValidateAction = {
  type: TransferDraftActionType.senderNameValidate;
  inputtedSenderName: string;
};

type FieldsPrefillAction = {
  type: TransferDraftActionType.prefillFields;
  fieldsPrefillData: FieldsPrefillData;
  environment: SkpEnvironment;
  mailboxDetails: MailboxDetails;
  // structured fields
  dynamicFormData?: Record<string, unknown> | null;
  prefillKeys?: {
    static: Array<"name" | "email" | "subject" | "description">;
    dynamic: string[];
  } | null;
};

type DynamicFormUpdateAction = {
  type: TransferDraftActionType.dynamicFormUpdate;
  data: Record<string, unknown> | null;
};

type ResetKeepPrefillAction = {
  type: TransferDraftActionType.resetKeepPrefill;
  environment: SkpEnvironment;
};

type TransferDraftAction =
  | InitializationAction
  | ResetAction
  | FilesAddAction
  | FilesRemoveAction
  | SenderNameValidateAction
  | SenderNameUpdateAction
  | SenderEmailUpdateAction
  | SubjectUpdateAction
  | DescriptionUpdateAction
  | ErrorUpdateAction
  | SenderEmailValidationAction
  | FieldsPrefillAction
  | DynamicFormUpdateAction
  | ResetKeepPrefillAction;

enum TransferDraftActionType {
  initialize,
  reset,
  validityExtension,
  filesAdd,
  filesRemove,
  senderNameUpdate,
  senderEmailUpdate,
  subjectUpdate,
  descriptionUpdate,
  senderNameValidate,
  senderEmailValidate,
  errorUpdate,
  acknowledgementUpdate,
  ttlUpdate,
  priorityUpdate,
  protectionTypeUpdate,
  passwordValidate,
  repeatPasswordValidate,
  passwordUpdate,
  repeatPasswordUpdate,
  prefillFields,
  dynamicFormUpdate,
  resetKeepPrefill,
}

function transferDraftReducer(
  transferDraft: TransferDraft,
  action: TransferDraftAction
): TransferDraft {
  switch (action.type) {
    case TransferDraftActionType.initialize:
      const details: MailboxDetails = action.mailboxDetails;
      const env: SkpEnvironment = action.environment;

      const owner: TransferReceiver = {
        realname: details.name ?? "",
        email: details.email,
        type: "to",
        uid: details.uid,
      };

      return {
        ...transferDraft,
        anon: {
          name: "",
          email: "",
        },
        subject: "",
        description: "",
        receivers: [owner],
        errors: new Set<TransferDraftErrors>(),
        acknowledgement: true,
        ttl: env.expiration.default,
        protectionType: ProtectionType.recipientAuthentication,
        prefilled: false,
        dynamicFormData: null,
      };
    case TransferDraftActionType.reset:
      return {
        ...transferDraft,
        subject: "",
        description: "",
        files: [],
        acknowledgement: true,
        ttl: action.environment.expiration.default,
        // The default priority is always normal unless the sender changes it.
        priority: 3,
        protectionType: ProtectionType.recipientAuthentication,
        anon: {
          name: "",
          email: "",
        },
        errors: new Set<TransferDraftErrors>(),
        password: "",
        repeatPassword: "",
        prefilled: false,
        dynamicFormData: null,
      };
    case TransferDraftActionType.filesAdd:
      // TODO: Remove this block when lifting the flag `REACT_APP_FF_NO_FILES_TRANSFER`
      if (!featureFlags.noFilesTransfer) {
        transferDraft.errors.delete(TransferDraftErrors.invalidFileCount);
      }
      return {
        ...transferDraft,
        files: [
          ...transferDraft.files,
          ...action.addedFiles.map(createTransferDraftFile),
        ],
      };
    case TransferDraftActionType.filesRemove:
      var filesToBeRemoved = action.removedFiles;
      while (filesToBeRemoved.length !== 0) {
        const fileToBeRemoved = filesToBeRemoved.pop()!;
        transferDraft.files = transferDraft.files.filter(
          (file) => file[0].id !== fileToBeRemoved[0].id
        );
      }
      return {
        ...transferDraft,
      };
    case TransferDraftActionType.senderNameUpdate:
      const senderName = action.updatedName.trim();

      // live clear error from ui
      if (!!senderName) {
        transferDraft.errors.delete(TransferDraftErrors.blankSenderName);
      }

      return {
        ...transferDraft,
        anon: {
          ...transferDraft.anon,
          name: action.updatedName,
        },
      };
    case TransferDraftActionType.senderEmailUpdate:
      const senderEmail = action.updatedEmail.trim();
      const emailMatch = senderEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/);

      // live clear error from ui
      if (!!senderEmail && emailMatch) {
        transferDraft.errors.delete(TransferDraftErrors.invalidSenderEmail);
      }

      return {
        ...transferDraft,
        anon: {
          ...transferDraft.anon,
          email: action.updatedEmail,
        },
      };
    case TransferDraftActionType.subjectUpdate:
      return {
        ...transferDraft,
        subject: action.updatedSubject,
      };
    case TransferDraftActionType.descriptionUpdate:
      return {
        ...transferDraft,
        description: action.updatedDescription,
      };
    case TransferDraftActionType.senderNameValidate:
      const trimmedSenderName = transferDraft.anon.name.trim();

      if (!trimmedSenderName) {
        transferDraft.errors.add(TransferDraftErrors.blankSenderName);
      } else {
        transferDraft.errors.delete(TransferDraftErrors.blankSenderName);
      }

      return { ...transferDraft };
    case TransferDraftActionType.senderEmailValidate:
      const trimmedSenderEmail = transferDraft.anon.email.trim();
      const senderEmailMatch = trimmedSenderEmail.match(
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      );
      if (senderEmailMatch === null) {
        transferDraft.errors.add(TransferDraftErrors.invalidSenderEmail);
      } else {
        transferDraft.errors.delete(TransferDraftErrors.invalidSenderEmail);
      }
      return { ...transferDraft };

    case TransferDraftActionType.errorUpdate:
      action.postValidationErrors.forEach((error) => {
        transferDraft.errors.add(error);
      });

      return {
        ...transferDraft,
      };
    case TransferDraftActionType.prefillFields: {
      const mailboxDetails: MailboxDetails = action.mailboxDetails;

      switch (action.fieldsPrefillData.type) {
        case FormPrefillType.replyPrefill: {
          const mailboxOwner: TransferReceiver = {
            realname: mailboxDetails.name ?? "",
            email: mailboxDetails.email,
            type: "to",
            uid: mailboxDetails.uid,
          };

          return {
            ...transferDraft,
            anon: {
              name: action.fieldsPrefillData.name ?? "",
              email: action.fieldsPrefillData.email ?? "",
            },
            receivers: [mailboxOwner],
            errors: new Set<TransferDraftErrors>(),
            acknowledgement: true,
            ttl: action.environment.expiration.default,
            protectionType: ProtectionType.recipientAuthentication,
            prefilled: true,
          };
        }

        case FormPrefillType.mailboxPrefill: {
          return {
            ...transferDraft,
            anon: {
              ...transferDraft.anon,
              name: action.fieldsPrefillData.name ?? transferDraft.anon.name,
              email: action.fieldsPrefillData.email ?? transferDraft.anon.email,
            },
            subject: action.fieldsPrefillData.subject ?? transferDraft.subject,
            description:
              action.fieldsPrefillData.description ?? transferDraft.description,
            // prefill structured fields
            dynamicFormData:
              typeof action.dynamicFormData !== "undefined"
                ? action.dynamicFormData
                : transferDraft.dynamicFormData,
            prefillKeys: action.prefillKeys ?? transferDraft.prefillKeys,
            prefilled: true,
          };
        }

        default:
          return transferDraft;
      }
    }

    case TransferDraftActionType.dynamicFormUpdate:
      return {
        ...transferDraft,
        dynamicFormData: action.data,
      };

    case TransferDraftActionType.resetKeepPrefill: {
      const keep = transferDraft.prefillKeys;

      // baseline reset (same as reset)
      const next: TransferDraft = {
        ...transferDraft,
        subject: "",
        description: "",
        files: [],
        acknowledgement: true,
        ttl: action.environment.expiration.default,
        priority: 3,
        protectionType: ProtectionType.recipientAuthentication,
        anon: { name: "", email: "" },
        errors: new Set<TransferDraftErrors>(),
        password: "",
        repeatPassword: "",
        prefilled: true,
        dynamicFormData: null,
      };

      if (!keep) {
        next.prefilled = false;
        return next;
      }

      // keep current values for fields that were originally prefilled
      if (keep.static.includes("name"))
        next.anon.name = transferDraft.anon.name;
      if (keep.static.includes("email"))
        next.anon.email = transferDraft.anon.email;
      if (keep.static.includes("subject")) next.subject = transferDraft.subject;
      if (keep.static.includes("description"))
        next.description = transferDraft.description;

      if (keep.dynamic.length > 0) {
        const current =
          transferDraft.dynamicFormData &&
          typeof transferDraft.dynamicFormData === "object"
            ? (transferDraft.dynamicFormData as Record<string, unknown>)
            : {};

        const filtered: Record<string, unknown> = {};
        keep.dynamic.forEach((k) => {
          if (Object.prototype.hasOwnProperty.call(current, k)) {
            filtered[k] = current[k];
          }
        });

        next.dynamicFormData = filtered;
      }

      return next;
    }
    default:
      assertNever(action);
  }
}

function createTransferDraftFile(file: File): TransferDraftFile {
  return [
    {
      id: crypto.randomUUID(),
      name: file.name,
      size: file.size,
    },
    file,
  ];
}

export { TransferDraftActionType, transferDraftReducer };
export type { TransferDraftAction };
